import React, { useState } from 'react';
import { Calendar, CheckCircle2 } from 'lucide-react';

const ChallengeCard = ({ definition, challenge, title }) => {
    // State to track expansion
    const [isExpanded, setIsExpanded] = useState(true);

    // Effect: When challenge data loads/changes, if it's completed, default to collapsed.
    // If it's not completed, ensure it's expanded.
    // We used a timeout to avoid immediate state updates during render clashes if any, standard safety.
    React.useEffect(() => {
        if (challenge?.completed) {
            setIsExpanded(false);
        } else {
            setIsExpanded(true);
        }
    }, [challenge?.completed]);

    if (!definition) return null;

    // Collapsed View (Completed)
    if (challenge?.completed && !isExpanded) {
        return (
            <div
                onClick={() => setIsExpanded(true)}
                className="bg-primary/5 px-6 py-4 rounded-xl border border-primary/10 shadow-sm cursor-pointer hover:bg-primary/10 transition-all flex items-center justify-between group min-h-[56px]"
                title="Click to expand details"
            >
                <div className="flex items-center gap-3">
                    <div className="text-primary/40 group-hover:text-primary/60 transition-colors">
                        <CheckCircle2 size={16} />
                    </div>
                    <div>
                        <div className="text-xs font-bold text-primary/70 uppercase tracking-wider group-hover:text-primary transition-colors">{title}</div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-xs text-muted hidden sm:block line-clamp-1 italic text-right max-w-[200px]">
                        {definition.description}
                    </span>
                    <div className="bg-primary/20 text-primary text-[10px] font-bold px-2 py-0.5 rounded-full border border-primary/20">
                        DONE
                    </div>
                </div>
            </div>
        );
    }

    // Expanded / Active View
    return (
        <div
            onClick={() => challenge?.completed && setIsExpanded(false)}
            className={`bg-gradient-to-br from-primary/20 to-primary/10 p-6 rounded-2xl border border-primary/30 shadow-lg relative overflow-hidden group transition-all ${challenge?.completed ? 'cursor-pointer hover:border-primary/50' : ''}`}
            title={challenge?.completed ? "Click to collapse" : ""}
        >
            <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:opacity-20 transition-opacity pointer-events-none">
                <Calendar size={64} />
            </div>

            <div className="flex justify-between items-start mb-2 relative z-10">
                <div>
                    <div className="text-xs font-bold text-primary uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Calendar size={12} /> {title}
                    </div>
                    <h3 className="text-lg font-bold text-main leading-tight">{definition.description}</h3>
                </div>
                {challenge?.completed ? (
                    <div className="bg-primary text-primary-content text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm flex-shrink-0">
                        <CheckCircle2 size={12} /> DONE
                    </div>
                ) : (
                    <div className="text-xs font-bold text-primary bg-primary/10 px-2 py-1 rounded-full border border-primary/30 whitespace-nowrap flex-shrink-0">
                        {challenge ? `${challenge.progress} / ${definition.target}` : 'Loading...'}
                    </div>
                )}
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-base/50 h-2 rounded-full overflow-hidden relative z-10">
                <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${Math.min(100, ((challenge?.progress || 0) / definition.target) * 100)}%` }}
                />
            </div>

            {challenge?.completed && (
                <div className="mt-2 text-xs text-primary font-bold flex items-center gap-1 animate-in fade-in slide-in-from-bottom-1">
                    🎉 Challenge Complete!
                </div>
            )}
        </div>
    );
};

const MenuChallenges = ({
    dailyChallenge,
    dailyDefinition,
    weeklyChallenge,
    weeklyDefinition
}) => {
    return (
        <div className="grid grid-cols-1 gap-8">
            <ChallengeCard
                definition={dailyDefinition}
                challenge={dailyChallenge}
                title="Daily Challenge"
            />
            <ChallengeCard
                definition={weeklyDefinition}
                challenge={weeklyChallenge}
                title="Weekly Challenge"
            />
        </div>
    );
};

export default MenuChallenges;
